"use client";

import { useEffect, useRef } from "react";

/** Portal commit is 1–2 frames; extra budget covers slow layout. */
const MAX_ATTACH_FRAMES = 32;

interface UseInfiniteScrollOptions {
  /** The scrollable viewport element to watch. */
  rootRef: React.RefObject<HTMLElement | null>;
  /** Whether there is another page to load. */
  hasNextPage: boolean;
  /** Whether a fetch is already in flight (prevents duplicate loads). */
  isFetching: boolean;
  /** Called when the user scrolls near the bottom and another page exists. */
  onLoadMore: () => void;
  /** Distance from the bottom (px) at which to start loading. */
  threshold?: number;
  /**
   * When false, the listener is not attached and no frames are scheduled.
   * Portaled pickers (`Select.List`) must pass the menu/dialog open flag so
   * a closed instance does not poll. Flip to true when the viewport is
   * about to mount; the hook retries a bounded number of frames until
   * `rootRef` attaches.
   */
  enabled?: boolean;
}

/**
 * Auto-load-more on scroll. Attaches a passive scroll listener to `rootRef`
 * (the dropdown's scroll viewport) and calls `onLoadMore` when the user nears
 * the bottom. Also fires once on mount so a short first page that doesn't fill
 * the viewport still pulls the next page. Replaces "Show more" buttons.
 */
export function useInfiniteScroll({
  rootRef,
  hasNextPage,
  isFetching,
  onLoadMore,
  threshold = 200,
  enabled = true,
}: UseInfiniteScrollOptions): void {
  // Latest flags/callback, read inside the listener without re-subscribing.
  const stateRef = useRef({ hasNextPage, isFetching, onLoadMore });
  useEffect(() => {
    stateRef.current = { hasNextPage, isFetching, onLoadMore };
  }, [hasNextPage, isFetching, onLoadMore]);

  useEffect(() => {
    if (!enabled) return;

    let el: HTMLElement | null = null;
    let raf = 0;
    let frames = 0;
    let cancelled = false;

    const maybeLoad = () => {
      if (!el) return;
      const { hasNextPage: more, isFetching: busy, onLoadMore: load } =
        stateRef.current;
      if (!more || busy) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
        load();
      }
    };

    const bind = (node: HTMLElement) => {
      el = node;
      node.addEventListener("scroll", maybeLoad, { passive: true });
      // Content may be shorter than the viewport on the first page.
      maybeLoad();
    };

    // Portaled dropdowns (Select.List) often mount after `enabled` flips, so
    // the first read of rootRef.current is null. Retry a few frames, then
    // stop — callers must set `enabled` from open state so we do not poll
    // while the menu is closed.
    const attach = () => {
      if (cancelled) return;
      const node = rootRef.current;
      if (!node) {
        if (frames >= MAX_ATTACH_FRAMES) return;
        frames += 1;
        raf = requestAnimationFrame(attach);
        return;
      }
      bind(node);
    };

    attach();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      el?.removeEventListener("scroll", maybeLoad);
    };
    // Re-bind when a new page arrives, when the viewport mounts (`enabled`),
    // and when hasNextPage/isFetching change so a closed-dropdown fetch still
    // attaches once the menu opens.
  }, [rootRef, hasNextPage, isFetching, threshold, enabled]);
}
